import { TZDate } from '@date-fns/tz';
import { addDays, addMonths, differenceInCalendarDays } from 'date-fns';

export const ISRAEL_TZ = 'Asia/Jerusalem';

type DateInput = Date | string | number;
const toDate = (d: DateInput) => (d instanceof Date ? d : new Date(d));

/** The calendar date in Israel, as "YYYY-MM-DD". */
export function israelDay(d: DateInput = new Date()): string {
  const t = new TZDate(toDate(d).getTime(), ISRAEL_TZ);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const day = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** A Date for the given Israel calendar day ("YYYY-MM-DD") at hh:mm Israel time. */
export function israelDateTime(day: string, hour = 9, minute = 0): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(new TZDate(y, m - 1, d, hour, minute, 0, ISRAEL_TZ).getTime());
}

/** Whole calendar days between two Israel days (b - a). */
export function daysBetween(aDay: string, bDay: string): number {
  return differenceInCalendarDays(new Date(bDay + 'T12:00:00Z'), new Date(aDay + 'T12:00:00Z'));
}

export function addDaysToDay(day: string, n: number): string {
  return addDays(new Date(day + 'T12:00:00Z'), n).toISOString().slice(0, 10);
}

export type FollowupPreset = 'tomorrow' | 'in3days' | 'week' | 'twoWeeks' | 'month';

export const FOLLOWUP_PRESETS: { key: FollowupPreset; label: string }[] = [
  { key: 'tomorrow', label: 'מחר' },
  { key: 'in3days', label: 'בעוד 3 ימים' },
  { key: 'week', label: 'שבוע' },
  { key: 'twoWeeks', label: 'שבועיים' },
  { key: 'month', label: 'חודש' },
];

/**
 * Due date for a follow-up preset, at 10:00 Israel time.
 * Friday evening/Saturday are not business days in Israel: a result on Saturday moves to Sunday.
 */
export function followupPresetDate(preset: FollowupPreset, now: DateInput = new Date(), hour = 10): Date {
  const today = israelDay(now);
  let day: string;
  switch (preset) {
    case 'tomorrow':
      day = addDaysToDay(today, 1);
      break;
    case 'in3days':
      day = addDaysToDay(today, 3);
      break;
    case 'week':
      day = addDaysToDay(today, 7);
      break;
    case 'twoWeeks':
      day = addDaysToDay(today, 14);
      break;
    case 'month':
      day = addMonths(new Date(today + 'T12:00:00Z'), 1).toISOString().slice(0, 10);
      break;
  }
  if (new Date(day + 'T12:00:00Z').getUTCDay() === 6) day = addDaysToDay(day, 1); // Saturday → Sunday
  return israelDateTime(day, hour);
}

export type DueBucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later';

/** Classify a due date against "now" using Israel calendar days. */
export function dueBucket(due: DateInput, now: DateInput = new Date()): DueBucket {
  const diff = daysBetween(israelDay(now), israelDay(due));
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff <= 7) return 'week';
  return 'later';
}

export const DUE_BUCKET_LABELS: Record<DueBucket, string> = {
  overdue: 'באיחור',
  today: 'היום',
  tomorrow: 'מחר',
  week: 'השבוע',
  later: 'בהמשך',
};

const dateFmt = new Intl.DateTimeFormat('he-IL', { timeZone: ISRAEL_TZ, day: '2-digit', month: '2-digit', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('he-IL', { timeZone: ISRAEL_TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const weekdayFmt = new Intl.DateTimeFormat('he-IL', { timeZone: ISRAEL_TZ, weekday: 'long' });

/** 27/09/2026 */
export function formatDate(d: DateInput | null | undefined): string {
  if (d === null || d === undefined || d === '') return '';
  // Plain dates ("YYYY-MM-DD") must not shift by timezone.
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  return dateFmt.format(toDate(d));
}

/** 27/09/2026 14:30 */
export function formatDateTime(d: DateInput | null | undefined): string {
  if (!d) return '';
  return `${formatDate(d)} ${timeFmt.format(toDate(d))}`;
}

export function formatTime(d: DateInput | null | undefined): string {
  if (!d) return '';
  return timeFmt.format(toDate(d));
}

export function formatWeekday(d: DateInput): string {
  return weekdayFmt.format(toDate(d));
}

/** "היום 14:30", "מחר 10:00", "אתמול", "לפני 5 ימים", "בעוד 3 ימים", or a date. */
export function formatRelativeDay(d: DateInput | null | undefined, now: DateInput = new Date(), withTime = true): string {
  if (!d) return '';
  const diff = daysBetween(israelDay(now), israelDay(d));
  const t = withTime ? ` ${formatTime(d)}` : '';
  if (diff === 0) return `היום${t}`;
  if (diff === 1) return `מחר${t}`;
  if (diff === -1) return `אתמול${t}`;
  if (diff < 0 && diff >= -30) return `לפני ${-diff} ימים`;
  if (diff > 0 && diff <= 14) return `בעוד ${diff} ימים`;
  return formatDate(d);
}
